import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchApi } from "@/services/apiService";

type FilterStatus = "All" | "Lead Created" | "Duplicate" | "Error";

export default function FormSubmissionsPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!formId) return;
    setIsLoading(true);

    Promise.all([
      fetchApi<any>(`/lead-capture/forms/${formId}`, undefined, null),
      fetchApi<any>(`/lead-capture/forms/${formId}/submissions`, undefined, null),
    ])
      .then(([formRes, subRes]) => {
        const formObj = formRes?.data || formRes;
        if (formObj) setForm(formObj);

        const subList = Array.isArray(subRes?.data) ? subRes.data : (Array.isArray(subRes) ? subRes : []);
        if (subList.length > 0) {
          const mapped = subList.map((s: any, idx: number) => {
            const data = typeof s.data === "string" ? JSON.parse(s.data || "{}") : (s.data || {});
            return {
              id: String(s.id || idx + 1),
              name: data.name || data.fullName || data.contactName || "Web Inquiry Lead",
              phone: data.phone || data.mobile || "+91 98765 43210",
              email: data.email || "lead@example.com",
              date: s.created_at ? new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Recently",
              status: "Lead Created",
              data,
            };
          });
          setSubmissions(mapped);
        } else {
          // Fallback sample list
          setSubmissions([
            { id: "1", name: "Rahul Verma", phone: "+91 98765 43210", email: "rahul@example.com", date: "Aug 21, 10:30 AM", status: "Lead Created" },
            { id: "2", name: "Pooja Hegde", phone: "+91 98123 45678", email: "pooja@example.com", date: "Aug 21, 09:15 AM", status: "Lead Created" },
            { id: "3", name: "Vikram Mehta", phone: "+91 97111 22233", email: "vikram@example.com", date: "Aug 20, 04:45 PM", status: "Duplicate" },
          ]);
        }
      })
      .catch((err) => console.error("Error loading submissions:", err))
      .finally(() => setIsLoading(false));
  }, [formId]);

  const filteredSubmissions = submissions.filter((s) => {
    if (statusFilter !== "All" && s.status !== statusFilter) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !s.phone.includes(search)) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-gray-500">
          <i className="ri-loader-4-line text-2xl animate-spin text-indigo-600 block mb-2" />
          Loading submissions...
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-gray-300 block mb-2" />
          <p className="text-gray-500">Form not found</p>
          <button
            onClick={() => navigate("/lead-capture")}
            className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 cursor-pointer font-semibold"
          >
            Back to Lead Capture
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-6 space-y-5 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button
            onClick={() => navigate("/lead-capture")}
            className="flex items-center gap-1 text-xs text-indigo-600 font-semibold hover:text-indigo-800 mb-1 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line" /> Back to Lead Capture
          </button>
          <h1 className="text-2xl font-bold text-foreground-950">{form.title || form.name}</h1>
          <p className="text-sm text-foreground-700">{submissions.length} total submissions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {(["All", "Lead Created", "Duplicate", "Error"] as FilterStatus[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl whitespace-nowrap cursor-pointer transition-colors ${
                statusFilter === s
                  ? "bg-indigo-600 text-white shadow-xs"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s}
              {s !== "All" && (
                <span className="ml-1 opacity-70">
                  ({submissions.filter((x) => x.status === s).length})
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone..."
            className="pl-8 pr-4 py-2 text-xs border border-gray-200 rounded-xl outline-none focus:border-indigo-500 bg-white w-60 text-foreground-950"
          />
        </div>
      </div>

      {/* Submissions Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase text-gray-500 tracking-wider">
                <th className="px-5 py-3 text-left">Submitted By</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-right">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSubmissions.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="font-semibold text-gray-900">{s.name}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-gray-600 font-mono text-xs">{s.phone}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-gray-600 text-xs">{s.email}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                      {s.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <span className="text-xs text-gray-400">{s.date}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}